// PRD §29 — Recommendation engine. Pure function over the report shape;
// no IO. Runs in the Worker today (aggregator.ts) and the same module
// would render in the M7 print route + M8 digest, so we keep it in
// /shared.
//
// Inputs come straight off the building ReportPayload — overview deltas,
// platform mix, UTM health score + issues. The engine emits at most
// MAX_RECOMMENDATIONS, sorted severe → medium → mild, with a stable tie
// break on category so consecutive runs over the same data produce the
// same order.

import type { CanonicalPlatform, IssueCategory, UtmIssue, UtmScoreResult } from "../utm/types";

const MAX_RECOMMENDATIONS = 5;

export type RecommendationSeverity = "severe" | "medium" | "mild";

export interface Recommendation {
  category: string;
  severity: RecommendationSeverity;
  message: string;
  /**
   * Optional call-to-action. When present, the in-app rec card renders it
   * as a secondary link beneath the message so users can jump straight to
   * the tool that resolves the issue — e.g. `canonical-naming` →
   * `/app/builder?medium=organic-social`, `utm-health` → `/app/utm-health`.
   */
  cta?: { label: string; href: string };
}

// M12.12 — "custom" joined the set so bounds-driven compare windows
// round-trip through the recommendation inputs. labelForCompare treats
// it identically to "previous" — the rule engine's narrative copy
// ("on the previous period") reads naturally whether the compare
// window is the derived prior N days or a user-picked custom window.
export type CompareMode = "previous" | "year_over_year" | "none" | "custom";

export interface RecommendationInputs {
  sessions: number;
  engagementRate: number;
  keyEvents: number;
  totalRevenue: number;
  /** Drives the wording of momentum/revenue copy ("previous period" vs
   * "the same period last year"). Defaults to "previous" if omitted so
   * existing call sites keep working. */
  compare?: CompareMode;
  deltas: {
    sessions: number | null;
    keyEvents: number | null;
    totalRevenue: number | null;
  };
  perPlatform: ReadonlyArray<{
    platform: CanonicalPlatform;
    sessions: number;
  }>;
  utmHealth: Pick<UtmScoreResult, "score" | "issues">;
}

export function buildRecommendations(input: RecommendationInputs): Recommendation[] {
  const out: Recommendation[] = [];
  const compareLabel = labelForCompare(input.compare);

  // Momentum — sessions delta vs the compare period.
  if (input.deltas.sessions !== null) {
    const d = input.deltas.sessions;
    if (d <= -0.25) {
      out.push({
        category: "momentum",
        severity: "severe",
        message: `Sessions are down ${formatPercentDrop(d)} ${compareLabel}. Investigate what changed in your top platforms.`,
      });
    } else if (d <= -0.1) {
      out.push({
        category: "momentum",
        severity: "medium",
        message: `Sessions slipped ${formatPercentDrop(d)} ${compareLabel}. A small re-test on your best-performing post types could pull this back.`,
      });
    } else if (d >= 0.25) {
      out.push({
        category: "momentum",
        severity: "mild",
        message: `Sessions grew ${formatPercentGain(d)} ${compareLabel}. Capture what changed so you can repeat it.`,
      });
    }
  }

  // Revenue cliff — only meaningful when there's revenue at all.
  if (
    input.totalRevenue > 0 &&
    input.deltas.totalRevenue !== null &&
    input.deltas.totalRevenue <= -0.3
  ) {
    out.push({
      category: "revenue",
      severity: "severe",
      message: `Revenue from social fell ${formatPercentDrop(input.deltas.totalRevenue)} ${compareLabel}. Double-check that your top converting platforms are still tagged correctly.`,
    });
  }

  // Engagement floor.
  if (input.sessions >= 100 && input.engagementRate < 0.5) {
    out.push({
      category: "engagement",
      severity: "medium",
      message: `Only ${(input.engagementRate * 100).toFixed(0)}% of social sessions are engaged. Review the landing pages your top platforms point at. Bounce often comes from a mismatch.`,
    });
  }

  // Conversion gap — sessions exist but key events are sparse.
  if (input.sessions >= 200 && input.keyEvents === 0) {
    out.push({
      category: "conversion",
      severity: "medium",
      message:
        "Plenty of sessions, no key events recorded. Confirm your key event configuration in GA4. If none fits, add one for the action that matters most to you.",
    });
  } else if (input.sessions >= 200 && input.keyEvents / input.sessions < 0.005) {
    out.push({
      category: "conversion",
      severity: "mild",
      message: `Conversion rate is ${((input.keyEvents / input.sessions) * 100).toFixed(2)}%, which is well under benchmark. Try a clearer call-to-action on your top landing page.`,
    });
  }

  // Platform concentration — meaningful only with 2+ platforms.
  const totalPlatformSessions = input.perPlatform.reduce((acc, r) => acc + r.sessions, 0);
  if (input.perPlatform.length >= 2 && totalPlatformSessions > 0) {
    const top = input.perPlatform.reduce((a, b) => (a.sessions > b.sessions ? a : b));
    const share = top.sessions / totalPlatformSessions;
    if (share >= 0.7) {
      out.push({
        category: "platform-mix",
        severity: "medium",
        message: `${top.platform} drives ${(share * 100).toFixed(0)}% of your social sessions. A single-platform bet is fragile. Test one secondary channel for resilience.`,
      });
    }
  }

  // UTM health — translate the scorer's diagnosis into an action. The
  // scorer's issue.detail describes WHAT it found; the user needs to know
  // WHAT TO DO. Surface up to two top issues when the score is mid-band so
  // the summary stays actionable rather than a wall of observations.
  if (input.utmHealth.score < 60) {
    out.push({
      category: "utm-health",
      severity: "severe",
      message: `UTM health is ${input.utmHealth.score}/100. Most of this report is being attributed from inconsistent or missing tags. Open the Tag health page, fix tagging at the source, and rerun this report before acting on the platform numbers.`,
      cta: { label: "Open Tag health", href: "/app/utm-health" },
    });
  } else if (input.utmHealth.score < 80 && input.utmHealth.issues.length > 0) {
    for (const issue of input.utmHealth.issues.slice(0, 2)) {
      out.push({
        category: "utm-health",
        severity: "medium",
        message: actionForUtmIssue(issue),
        cta: ctaForUtmIssue(issue),
      });
    }
  }

  return sortAndCap(out);
}

/**
 * Map a UtmIssue category to the most relevant deep-link in the app — the
 * page or Builder preset that fixes the class of problem. Returns
 * undefined for categories that don't have a natural destination.
 */
function ctaForUtmIssue(issue: UtmIssue): { label: string; href: string } | undefined {
  switch (issue.category) {
    case "canonical-naming":
      return { label: "Open UTM builder", href: "/app/builder?medium=organic-social" };
    case "required-params":
    case "campaign-structure":
    case "platform-metadata":
    case "content-granularity":
    case "formatting":
    case "anti-pattern":
      return { label: "Open UTM builder", href: "/app/builder" };
    case "vocabulary":
      // Drop the marketer on the taxonomy page so they can see the
      // active allowlist before they reopen the builder.
      return { label: "View taxonomy", href: "/app/utm-health/taxonomy" };
    case "dark-social-leakage":
      return { label: "Open Tag health", href: "/app/utm-health" };
    default:
      return undefined;
  }
}

/**
 * Map a UtmIssue diagnosis to an action the user can take. The scorer
 * tells us what's wrong; this turns it into "do X". Falls back to the
 * raw label only when the category is unknown.
 */
function actionForUtmIssue(issue: UtmIssue): string {
  const examples = issue.examples.slice(0, 2).join(", ");
  const tail = examples ? ` Examples in your data: ${examples}.` : "";
  const advice = ADVICE_BY_CATEGORY[issue.category];
  if (!advice) return issue.detail || issue.label;
  return `${issue.label}. ${advice}${tail}`;
}

export const ADVICE_BY_CATEGORY: Record<IssueCategory, string> = {
  "required-params":
    "Update your link templates so every social URL carries utm_source, utm_medium, and utm_campaign. Backfill the affected campaigns at the source platform if possible.",
  "canonical-naming":
    "Rewrite the offending tags to one of the canonical mediums (organic-social / paid-social / influencer / community). The UTM builder on /app/builder will generate the right shape.",
  "campaign-structure":
    "Standardise your utm_campaign naming. A short pattern like q1-free-trial or apr-newsletter keeps campaigns groupable in the leaderboard.",
  "platform-metadata":
    "Move the platform name into utm_source (instagram, tiktok, etc.) and reserve utm_source_platform for paid traffic. The leaderboard groups by canonical source.",
  "content-granularity":
    "Add utm_content to your highest-volume campaigns so you can attribute creative variants here. Even a single value (eg. story-v1) unlocks A/B reporting later.",
  "dark-social-leakage":
    "Wrap shared URLs with a click-tracker or referrer that injects UTM params. Without it, sessions arriving via DMs and copy-paste land as 'direct' and won't appear in this report.",
  formatting:
    "Lowercase every UTM value and use hyphens (not spaces or underscores) between words. Avoid URL-unsafe characters (& ? # % = +). Keeps the leaderboard from splitting one campaign across multiple casings.",
  vocabulary:
    "Use values from the active naming-convention vocabulary. The taxonomy page lists the allowlist for utm_source and utm_medium. Pick the closest match (e.g. 'social' → 'organic-social' or 'paid-social').",
  "anti-pattern":
    "Avoid placeholder names (campaign1, test, may-ads) and YYYYMMDD-style dates. Prefer descriptive shapes like q1-free-trial, apr-newsletter, blackfriday-2025-sale.",
};

function sortAndCap(recs: Recommendation[]): Recommendation[] {
  const rank: Record<RecommendationSeverity, number> = { severe: 0, medium: 1, mild: 2 };
  return [...recs]
    .sort((a, b) => rank[a.severity] - rank[b.severity] || a.category.localeCompare(b.category))
    .slice(0, MAX_RECOMMENDATIONS);
}

function formatPercentDrop(d: number): string {
  return `${Math.abs(d * 100).toFixed(0)}%`;
}

function formatPercentGain(d: number): string {
  return `${(d * 100).toFixed(0)}%`;
}

function labelForCompare(mode: CompareMode | undefined): string {
  if (mode === "year_over_year") return "vs the same period last year";
  // "previous" is the historic default; "none" never reaches the delta
  // branches because deltas are null in that case, so fallback is fine.
  return "on the previous period";
}
